import type { EmaProceduralStepItem } from '../../types/dtos/ema';
import type {
  ProceduralStepDraft,
  ProceduralStepIndicationStatus,
  ProceduralStepsDialog,
  ProceduralStepsReviewAction,
  ProceduralStepsReviewState
} from './ProceduralStepsEMAReview.types';

const INDICATION_STATUSES: ProceduralStepIndicationStatus[] = ['Qced', 'To Escalate', 'To QC'];

const toIndicationStatus = (value?: string | null): ProceduralStepIndicationStatus | '' =>
  INDICATION_STATUSES.find(status => status === value) ?? '';

const EMPTY_DRAFT: ProceduralStepDraft = {
  applNo: '',
  scope: '',
  opinion: '',
  commissionDecision: '',
  productInfoAffected: '',
  summary: '',
  indicationStatus: '',
  status: ''
};

export const createInitialState = (): ProceduralStepsReviewState => ({
  loading: false,
  steps: [],
  total: 0,
  sessionNewCount: 0,
  selectedStepId: null,
  query: '',
  dropdownOpen: false,
  mode: 'view',
  draft: EMPTY_DRAFT,
  pdfUrl: '',
  sourceUrl: '',
  dialogs: {
    submit: false,
    duplicate: false,
    done: false
  }
});

const toDraft = (step: EmaProceduralStepItem): ProceduralStepDraft => ({
  applNo: step.appln_no || '',
  scope: step.scope || '',
  opinion: step.bookmark_0 || '',
  commissionDecision: step.commission_decision || 'n/a',
  productInfoAffected: step.bookmark_2 || '',
  summary: step.summary || '',
  indicationStatus: toIndicationStatus(step.indication_status),
  status: step.status || 'incomplete'
});

const setDialog = (
  state: ProceduralStepsReviewState,
  dialog: ProceduralStepsDialog,
  open: boolean
): ProceduralStepsReviewState => ({
  ...state,
  dialogs: {
    ...state.dialogs,
    [dialog]: open
  }
});

export const proceduralStepsReviewReducer = (
  state: ProceduralStepsReviewState,
  action: ProceduralStepsReviewAction
): ProceduralStepsReviewState => {
  switch (action.type) {
    case 'LOAD_STARTED':
      return { ...state, loading: true };
    case 'LOAD_SUCCEEDED':
      return {
        ...state,
        ...action.value,
        loading: false
      };
    case 'LOAD_FAILED':
      return { ...state, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.value };
    case 'SET_QUERY':
      return { ...state, query: action.value, dropdownOpen: true };
    case 'CHOOSE_QUERY':
      return { ...state, query: action.value, dropdownOpen: false };
    case 'SET_DROPDOWN':
      return { ...state, dropdownOpen: action.value };
    case 'SELECT_STEP':
      return {
        ...state,
        selectedStepId: action.value.id,
        draft: toDraft(action.value),
        dropdownOpen: false,
        mode: 'view'
      };
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedStepId: null,
        draft: EMPTY_DRAFT,
        dropdownOpen: false
      };
    case 'START_ADD':
      return {
        ...state,
        selectedStepId: null,
        query: '',
        draft: EMPTY_DRAFT,
        dropdownOpen: false,
        mode: 'add'
      };
    case 'START_EDIT':
      return { ...state, mode: 'edit' };
    case 'CANCEL_CHANGES':
      return {
        ...state,
        mode: 'view',
        draft: action.resetDraft ? EMPTY_DRAFT : state.draft
      };
    case 'UPDATE_DRAFT':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.field]: action.value
        }
      };
    case 'OPEN_DIALOG':
      return setDialog(state, action.value, true);
    case 'CLOSE_DIALOG':
      return setDialog(state, action.value, false);
    case 'SAVE_SUCCEEDED':
      return {
        ...state,
        loading: true,
        selectedStepId: null,
        query: '',
        dropdownOpen: false,
        mode: 'view',
        draft: EMPTY_DRAFT,
        sessionNewCount: state.sessionNewCount + (action.wasAdd ? 1 : 0),
        dialogs: {
          ...state.dialogs,
          submit: false,
          duplicate: false
        }
      };
    default:
      return state;
  }
};

export const getDocumentUrls = (
  steps: EmaProceduralStepItem[],
  responsePdfUrl?: string | null
): { pdfUrl: string; sourceUrl: string } => {
  const rowWithPdf = steps.find(step => step.url);
  const sourceRow = rowWithPdf || steps.find(step => step.sourced_from || step.trigger_s3_path);
  const pdfUrl = responsePdfUrl || rowWithPdf?.url || '';
  const sourceUrl =
    sourceRow?.sourced_from ||
    sourceRow?.trigger_s3_path ||
    pdfUrl.split('?')[0].split('.com/')[1] ||
    pdfUrl;

  return { pdfUrl, sourceUrl };
};

export const isDraftValid = (draft: ProceduralStepDraft): boolean =>
  Boolean(draft.applNo.trim() && draft.scope.trim());
